import { Gauge, Zap, Fuel, ArrowRight } from "lucide-react";

const STAT_ICON_SIZE = 22;
const INLINE_ICON_SIZE = 14;

const StatCard = ({ icon: Icon, label, value, unit, subText, barWidth, barColor = "from-[#FF3B30] to-[#FF6B30]", muted }) => (
  <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#2A2A2A]">
    <div className="flex items-center gap-3 mb-4">
      <Icon size={STAT_ICON_SIZE} className={muted ? "text-[#8A8A8A]" : "text-[#FF3B30]"} />
      <span className="text-sm text-[#8A8A8A] uppercase tracking-wider">{label}</span>
    </div>
    <div className="flex items-end gap-3 mb-3">
      <span className="text-4xl font-bold text-white">{value}</span>
      <span className="text-lg text-[#8A8A8A] mb-1">{unit}</span>
    </div>
    {subText && <div className="flex items-center gap-2 text-sm">{subText}</div>}
    {barWidth && (
      <div className="mt-4 h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
        <div className={`h-full rounded-full bg-gradient-to-r ${barColor}`} style={{ width: barWidth }} />
      </div>
    )}
    {muted && <p className="text-[#8A8A8A] text-xs mt-3">Original factory {label.toLowerCase()} output</p>}
  </div>
);

export default function StageDisplay({ stage, results }) {
  if (stage === "3") {
    return (
      <div className="bg-[#0A0A0A] rounded-lg p-8 border border-[#9B59B6]/30 text-center">
        <h4 className="text-[#9B59B6] text-lg font-semibold mb-3">Stage 3 — Custom Tune</h4>
        <p className="text-white text-2xl font-bold mb-4">On Request Only</p>
        <p className="text-[#8A8A8A] text-sm leading-relaxed max-w-lg mx-auto">
          Stage 3 requires custom hardware upgrades. Power gains are calculated individually based on your specific setup and goals.
        </p>
        <div className="mt-4 flex items-center justify-center gap-3 text-sm text-[#8A8A8A]">
          <span>Base: {results.original_hp} HP / {results.original_torque} Nm</span>
        </div>
      </div>
    );
  }

  if (stage === "stock") {
    return (
      <div className="grid sm:grid-cols-2 gap-6">
        <StatCard icon={Gauge} label="Stock Power" value={results.original_hp} unit="HP" muted />
        <StatCard icon={Zap} label="Stock Torque" value={results.original_torque} unit="Nm" muted />
      </div>
    );
  }

  const hpBar = `${Math.min(100, ((results.tuned_hp || 0) / ((results.original_hp || 1) * 1.5)) * 100)}%`;
  const tqBar = `${Math.min(100, ((results.tuned_torque || 0) / ((results.original_torque || 1) * 1.5)) * 100)}%`;

  return (
    <>
      <div className="grid sm:grid-cols-2 gap-6">
        <StatCard
          icon={Gauge} label="Power" value={results.tuned_hp} unit="HP" barWidth={hpBar}
          subText={<><span className="text-[#8A8A8A]">Stock: {results.original_hp} HP</span><ArrowRight size={INLINE_ICON_SIZE} className="text-[#FF3B30]" /><span className="text-[#FF3B30] font-semibold">+{results.hp_gain} HP</span></>}
        />
        <StatCard
          icon={Zap} label="Torque" value={results.tuned_torque} unit="Nm" barWidth={tqBar}
          subText={<><span className="text-[#8A8A8A]">Stock: {results.original_torque} Nm</span><ArrowRight size={INLINE_ICON_SIZE} className="text-[#FF3B30]" /><span className="text-[#FF3B30] font-semibold">+{results.torque_gain} Nm</span></>}
        />
      </div>

      {/* Fuel Efficiency */}
      <div data-testid="fuel-efficiency-note" className="mt-6">
        <StatCard
          icon={Fuel} label="Fuel Efficiency"
          value={results.fuel_type === "Diesel" ? "5-15" : "3-8"} unit="% savings"
          barWidth={results.fuel_type === "Diesel" ? "65%" : "45%"}
          barColor="from-[#6AB04C] to-[#2D6A4F]"
          subText={
            <>
              <span className="text-[#8A8A8A]">Under normal driving conditions</span>
              <ArrowRight size={INLINE_ICON_SIZE} className="text-[#6AB04C]" />
              <span className="text-[#6AB04C] font-semibold">{results.fuel_type === "Diesel" ? "Diesel" : "Petrol"} optimized</span>
            </>
          }
        />
        <p className="text-[#6A6A6A] text-xs mt-3 px-6">
          {results.fuel_type === "Diesel"
            ? "Optimized injection timing and improved torque delivery reduce fuel consumption."
            : "Optimized fueling maps and improved engine efficiency reduce fuel consumption."}
          {stage === "2" && " Stage 2 results depend on hardware modifications and driving style."}
        </p>
      </div>
    </>
  );
}
